import { useState, type ReactNode } from 'react';
import { IconButton, Sheet } from '../../ui';
import { useTranslation } from 'react-i18next';
import { PRODUCT_NAME } from '@os/shared';
import { useApi } from '../../app/providers';
import { href, matchRoute, navigate, useLocation, type RouteMatch } from '../../app/router';
import { useOrg, useProjects, useRecents } from './queries';
import { useTheme } from '../../app/theme-store';
import { CommandPalette } from '../palette/CommandPalette';
import type { PaletteCommand } from '../palette/commands';
import { PaletteRegistry, usePaletteShortcut, useRegisteredCommands } from '../palette/registry';
import { useSharedWithMe } from './SharedPage';
import { cn } from '../../ui/cn';
import { Icon } from '../../ui/icons';
import { UndoProvider } from './undo';
import { Clock, House, LayoutTemplate, Menu as MenuIcon, Settings, Star, Trash2, Users } from 'lucide-react';
import { Sidebar, useCollapsed } from './sidebar/Sidebar';

const NAV = [
  { to: href('home'), key: 'dashboard.home', org: true, icon: House },
  { to: href('recent'), key: 'dashboard.recent', org: true, icon: Clock },
  { to: href('shared'), key: 'dashboard.shared', org: false, icon: Users },
  { to: href('favorites'), key: 'dashboard.favorites', org: true, icon: Star },
  { to: href('templates'), key: 'dashboard.templates', org: true, icon: LayoutTemplate },
  { to: href('trash'), key: 'dashboard.trash', org: true, icon: Trash2 },
  { to: href('settings'), key: 'dashboard.settings', org: false, icon: Settings },
] as const;


/** Which dashboard page a route shows (P15c): the page fades in when this changes, the query string never counts. */
export function pageKey(route: RouteMatch | null): string {
  return route ? [route.name, ...Object.values(route.params)].join(':') : 'none';
}

/** Dashboard frame: sidebar, search and the ⌘K palette (its commands come from here and from the pages). */
export function DashboardLayout({ children }: { children: ReactNode }) {
  return (
    <PaletteRegistry>
      <UndoProvider>
        <Layout>{children}</Layout>
      </UndoProvider>
    </PaletteRegistry>
  );
}

const THEMES = [
  ['system', 'settings.themeSystem'],
  ['light', 'settings.themeLight'],
  ['dark', 'settings.themeDark'],
] as const;

function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const api = useApi();
  const { path } = useLocation();
  const { org } = useOrg();
  const shared = useSharedWithMe();
  // Members see "Shared with me" only when something is; externals (no organization) see only that and settings.
  const nav = NAV.filter((item) => (item.org ? Boolean(org) : item.key !== 'dashboard.shared' || !org || (shared.data?.length ?? 0) > 0));

  const recents = useRecents();
  const projects = useProjects(org?.id);
  const [, setTheme] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const registered = useRegisteredCommands();
  usePaletteShortcut(() => setPaletteOpen(true));
  const paletteCommands = (): PaletteCommand[] => [
    ...nav.map((item): PaletteCommand => ({ id: `nav:${item.key}`, label: t(item.key), group: 'navigate', run: () => navigate(item.to) })),
    ...registered(),
    ...(recents.data ?? []).slice(0, 8).map(({ board: b }): PaletteCommand => ({ id: `board:${b.id}`, label: b.title, group: 'navigate', keywords: ['board', 'tableau'], run: () => navigate(href('board', { boardId: b.id })) })),
    ...(projects.data ?? []).map((p): PaletteCommand => ({ id: `project:${p.id}`, label: p.name, group: 'navigate', keywords: ['project', 'projet', ...(p.clientName ? [p.clientName] : [])], run: () => navigate(href('project', { projectId: p.id })) })),
    ...THEMES.map(([value, key]): PaletteCommand => ({ id: `theme:${value}`, label: t('palette.theme', { name: t(key) }), group: 'app', keywords: ['appearance', 'apparence'], run: () => setTheme(value) })),
    { id: 'sign-out', label: t('auth.signOut'), group: 'app', run: () => void api.auth.signOut() },
  ];

  const [collapsed, setCollapsed] = useCollapsed();
  const [sheet, setSheet] = useState(false);
  // The phone's sheet closes once a link inside it has navigated.
  const [openedAt, setOpenedAt] = useState(path);
  if (sheet && path !== openedAt) {
    setSheet(false);
    setOpenedAt(path);
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg md:flex-row">
      <aside className={cn('hidden shrink-0 bg-bg md:sticky md:top-0 md:flex md:h-dvh', collapsed ? 'md:w-16' : 'md:w-64')}>
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </aside>
      <header className="flex items-center gap-2 bg-bg px-4 py-2 md:hidden">
        <IconButton
          label={t('dashboard.sidebar.menu')}
          icon={<Icon icon={MenuIcon} size={20} />}
          onClick={() => {
            setOpenedAt(path);
            setSheet(true);
          }}
        />
        <p className="font-display text-base font-semibold text-fg">{PRODUCT_NAME}</p>
      </header>
      {sheet && (
        <Sheet title={PRODUCT_NAME} closeLabel={t('sharing.close')} onClose={() => setSheet(false)}>
          <Sidebar collapsed={false} inSheet />
        </Sheet>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-10 px-4 py-6 md:px-12 md:py-10">
        <main key={pageKey(matchRoute(path))} className="os-route flex flex-col gap-12">
          {children}
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={paletteOpen ? paletteCommands() : []} />
    </div>
  );
}
